import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Lock, Crown, Star, Shirt, Zap, Award, Coins, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { playClick, playCoin } from "../game/sound";
import { useAuth } from "../contexts/AuthContext";
import { fetchCosmeticsCatalog, equipCosmetic, purchaseCosmetic } from "../game/authService";

const SLOTS = [
  { id: "ufo_skin",    label: "UFO Skin",     Icon: Shirt },
  { id: "beam_effect", label: "Beam Effect",  Icon: Zap },
  { id: "badge",       label: "Badge",        Icon: Star },
  { id: "title",       label: "Title",        Icon: Award },
];

function TierTag({ tier }) {
  if (tier === "founder") {
    return <span className="cos-tier cos-tier-founder"><Crown size={10} /> FOUNDER</span>;
  }
  if (tier === "vip") {
    return <span className="cos-tier cos-tier-vip"><Star size={10} /> VIP</span>;
  }
  if (tier === "shop") {
    return <span className="cos-tier cos-tier-shop"><ShoppingCart size={10} /> SHOP</span>;
  }
  return null;
}

// Tiny inline SVG previews per UFO shape so the player can see the silhouette
// difference at a glance (without spinning up R3F inside the menu).
function UfoSvgShape({ shape, body, dome, glow }) {
  const common = { width: 56, height: 40, viewBox: "0 0 56 40" };
  if (shape === "triangle") {
    return (
      <svg {...common}>
        <polygon points="28,6 50,30 6,30" fill={body} stroke={glow} strokeWidth="1.5" />
        <ellipse cx="28" cy="22" rx="6" ry="3.5" fill={dome} opacity="0.85" />
        <circle cx="12" cy="30" r="2" fill={glow} />
        <circle cx="44" cy="30" r="2" fill={glow} />
      </svg>
    );
  }
  if (shape === "rocket") {
    return (
      <svg {...common}>
        <ellipse cx="28" cy="22" rx="8" ry="14" fill={body} />
        <path d="M20 22 Q28 4 36 22 Z" fill={dome} />
        <circle cx="28" cy="20" r="3" fill={glow} />
        <polygon points="20,32 14,38 22,34" fill={body} />
        <polygon points="36,32 42,38 34,34" fill={body} />
        <ellipse cx="28" cy="38" rx="3" ry="2" fill={glow} opacity="0.85" />
      </svg>
    );
  }
  if (shape === "crystal") {
    return (
      <svg {...common}>
        <polygon points="28,4 48,20 28,36 8,20" fill={body} opacity="0.75" />
        <polygon points="28,10 40,20 28,30 16,20" fill={dome} opacity="0.95" />
        <ellipse cx="28" cy="20" rx="22" ry="4" fill="none" stroke={glow} strokeWidth="1.5" opacity="0.7" />
      </svg>
    );
  }
  if (shape === "orb") {
    return (
      <svg {...common}>
        <circle cx="28" cy="20" r="14" fill={body} opacity="0.6" />
        <circle cx="28" cy="20" r="7" fill={dome} />
        <ellipse cx="28" cy="20" rx="20" ry="3.5" fill="none" stroke={glow} strokeWidth="1.5" />
        <ellipse cx="28" cy="20" rx="17" ry="3" fill="none" stroke={dome} strokeWidth="1" transform="rotate(20 28 20)" />
      </svg>
    );
  }
  if (shape === "battle") {
    return (
      <svg {...common}>
        <rect x="4" y="14" width="48" height="12" rx="2" fill={body} />
        <rect x="20" y="9" width="16" height="6" rx="1" fill={dome} />
        <rect x="22" y="25" width="12" height="4" fill="#222" />
        <rect x="24" y="29" width="2" height="6" fill="#444" />
        <rect x="30" y="29" width="2" height="6" fill="#444" />
        <circle cx="10" cy="22" r="1.5" fill={glow} />
        <circle cx="46" cy="22" r="1.5" fill={glow} />
      </svg>
    );
  }
  // Default: classic saucer
  return (
    <svg {...common}>
      <ellipse cx="28" cy="24" rx="22" ry="6" fill={body} />
      <path d="M12 24 Q28 6 44 24 Z" fill={dome} opacity="0.9" />
      <ellipse cx="28" cy="30" rx="18" ry="3" fill={glow} opacity="0.4" />
      <circle cx="14" cy="24" r="1.5" fill="#fff66b" />
      <circle cx="28" cy="24" r="1.5" fill={glow} />
      <circle cx="42" cy="24" r="1.5" fill="#fff66b" />
    </svg>
  );
}

function Preview({ cosmetic }) {
  const v = cosmetic.visual || {};
  if (cosmetic.slot === "ufo_skin") {
    return (
      <div
        className="cos-preview cos-preview-ufo"
        style={{
          background: `radial-gradient(circle at 50% 65%, ${v.glow}33, transparent 70%), linear-gradient(160deg, #1a1330, #0c0820)`,
          boxShadow: `inset 0 0 20px ${v.glow}44`,
        }}
      >
        <UfoSvgShape shape={v.shape || "saucer"} body={v.body} dome={v.dome} glow={v.glow} />
      </div>
    );
  }
  if (cosmetic.slot === "beam_effect") {
    return (
      <div
        className="cos-preview cos-preview-beam"
        style={{ background: `linear-gradient(180deg, ${v.color}66, transparent), ${v.emissive}` }}
      />
    );
  }
  if (cosmetic.slot === "badge") {
    return (
      <div
        className="cos-preview cos-preview-badge"
        style={{ background: `linear-gradient(90deg, ${v.color1}, ${v.color2})` }}
      >
        {v.label || "—"}
      </div>
    );
  }
  return (
    <div className="cos-preview cos-preview-title" style={{ color: v.color || "#999" }}>
      {v.text || "—"}
    </div>
  );
}

export default function CosmeticsScreen({ onBack }) {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchCosmeticsCatalog().then(setCatalog);
  }, []);

  const owned = useMemo(() => new Set(user?.owned_cosmetics || []), [user]);
  const equipped = user?.equipped || {};
  const coins = user?.coins || 0;

  const equip = async (slot, cosmeticId) => {
    setErr("");
    setBusy(`${slot}:${cosmeticId}`);
    playClick();
    try {
      await equipCosmetic(slot, cosmeticId);
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not equip");
    } finally {
      setBusy(null);
    }
  };

  const buy = async (cosmetic) => {
    setErr("");
    setBusy(`buy:${cosmetic.id}`);
    playClick();
    try {
      const res = await purchaseCosmetic(cosmetic.id);
      await refresh();
      playCoin();
      toast.success(`${cosmetic.name} unlocked!`, {
        description: `Spent ${res.coins_spent} coins — ${res.coins_remaining} remaining.`,
        duration: 3500,
      });
      // Auto-equip the freshly-bought item if no item is equipped in that slot.
      if (!equipped[cosmetic.slot]) {
        await equipCosmetic(cosmetic.slot, cosmetic.id);
        await refresh();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Purchase failed";
      setErr(detail);
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  if (!user) {
    return (
      <div className="screen placeholder-screen">
        <button type="button" className="zc-btn icon-back" onClick={() => { playClick(); onBack(); }}>
          <ArrowLeft size={18} /> Back
        </button>
        <div className="placeholder-wrap">
          <h1 className="placeholder-title">Cosmetics</h1>
          <p className="placeholder-blurb">Sign in with Google to manage your cosmetics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen cosmetics-screen" data-testid="cosmetics-screen">
      <button type="button" className="zc-btn icon-back" onClick={() => { playClick(); onBack(); }}>
        <ArrowLeft size={18} /> Back
      </button>
      <div className="cos-wrap">
        <div className="cos-header-row">
          <h1 className="cos-title"><Shirt size={26} /> Cosmetics</h1>
          <div className="cos-coin-pill" data-testid="cosmetics-coin-balance">
            <Coins size={16} /> {coins.toLocaleString()} coins
          </div>
        </div>
        <p className="cos-tag">Spend coins on shop items. Earn coins by collecting zombies. VIP & Founder cosmetics unlock automatically.</p>

        {SLOTS.map(({ id: slotId, label, Icon }) => {
          const items = catalog.filter((c) => c.slot === slotId);
          return (
            <section key={slotId} className="cos-section" data-testid={`cos-slot-${slotId}`}>
              <div className="cos-section-head">
                <Icon size={18} />
                <h2>{label}</h2>
                <span className="cos-section-equipped">
                  Equipped: <b>{catalog.find((c) => c.id === equipped[slotId])?.name || "—"}</b>
                </span>
              </div>
              <div className="cos-grid">
                {items.map((c) => {
                  const isOwned = owned.has(c.id) || c.tier === "free"
                    || (c.tier === "vip" && (user.is_vip))
                    || (c.tier === "founder" && (user.founder));
                  const isEquipped = equipped[slotId] === c.id;
                  const price = c.price || 0;
                  const canAfford = coins >= price;
                  return (
                    <div
                      key={c.id}
                      className={`cos-card ${isOwned ? "" : "locked"} ${isEquipped ? "equipped" : ""}`}
                      data-testid={`cos-card-${c.id}`}
                    >
                      <Preview cosmetic={c} />
                      <div className="cos-name">{c.name}</div>
                      <TierTag tier={c.tier} />
                      {!isOwned && c.tier === "shop" && (
                        <button
                          type="button"
                          className="zc-btn cos-buy"
                          disabled={!canAfford || busy === `buy:${c.id}`}
                          onClick={() => buy(c)}
                          data-testid={`cos-buy-${c.id}`}
                          title={canAfford ? `Buy for ${price} coins` : `Need ${price - coins} more coins`}
                        >
                          {busy === `buy:${c.id}` ? "Buying…" : (
                            <>
                              <Coins size={13} /> {price.toLocaleString()}
                            </>
                          )}
                        </button>
                      )}
                      {!isOwned && c.tier !== "shop" && (
                        <div className="cos-lock"><Lock size={12} /> {c.tier === "founder" ? "Founder only" : "VIP only"}</div>
                      )}
                      {isOwned && !isEquipped && (
                        <button
                          type="button"
                          className="zc-btn cos-equip"
                          disabled={busy === `${slotId}:${c.id}`}
                          onClick={() => equip(slotId, c.id)}
                          data-testid={`cos-equip-${c.id}`}
                        >
                          Equip
                        </button>
                      )}
                      {isEquipped && (
                        <div className="cos-equipped-badge" data-testid={`cos-equipped-${c.id}`}>
                          <Check size={14} /> Equipped
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        {err && <div className="vip-err">{err}</div>}
      </div>
    </div>
  );
}
